(function (global) {
  "use strict";

  const configurationCache = new Map();
  const lineSetOptionPattern = /line\s*set|lineset/i;

  function positiveNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function numericVariantId(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const match = String(value || "").match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  function normalizeLineSetLength(value) {
    const length = positiveNumber(value);
    if (!length) return 0;
    return length === 35 ? 33 : length;
  }

  function parseLineSetLength(value) {
    if (value === 0) return 0;
    const text = value == null ? "" : String(value);
    if (/without\s+line\s*set|^none$/i.test(text.trim())) return 0;
    const match = text.match(/(\d+)\s*(?:ft|feet)/i);
    return match ? Number(match[1]) : null;
  }

  function hasLineSetSelection(value) {
    return parseLineSetLength(value) !== null || positiveNumber(value) !== null;
  }

  function parseIndoorBtu(value) {
    const text = String(value || "");
    const kiloMatch = text.match(/(\d+(?:\.\d+)?)\s*k\b/i);
    if (kiloMatch) return Math.round(Number(kiloMatch[1]) * 1000);
    const btuMatch = text.match(/(\d{4,5})\s*btu\b/i);
    return btuMatch ? Number(btuMatch[1]) : null;
  }

  function closestAvailableLineSet(items, requestedLength, getLength) {
    const requested = normalizeLineSetLength(requestedLength);
    const candidates = (items || []).map(function (item) {
      return {
        item: item,
        length: normalizeLineSetLength(getLength(item)),
      };
    }).filter(function (candidate) {
      return candidate.item && candidate.item.available !== false && candidate.length > 0;
    });
    const exact = candidates.find(function (candidate) {
      return candidate.length === requested;
    });
    if (exact) return Object.assign({ substituted: false }, exact);
    const longer = candidates.filter(function (candidate) {
      return candidate.length > requested;
    }).sort(function (left, right) {
      return left.length - right.length || Number(left.item.priceCents || 0) - Number(right.item.priceCents || 0);
    });
    return longer.length ? Object.assign({ substituted: true }, longer[0]) : null;
  }

  function lineSetSubstitution(roomIndex, requestedLength, replacementLength) {
    return {
      roomIndex: Number(roomIndex) || 1,
      requestedLengthFt: normalizeLineSetLength(requestedLength),
      replacementLengthFt: normalizeLineSetLength(replacementLength),
      message:
        normalizeLineSetLength(requestedLength) +
        " ft is temporarily unavailable for Room " +
        (Number(roomIndex) || 1) +
        ". We selected the available " +
        normalizeLineSetLength(replacementLength) +
        " ft line set instead.",
    };
  }

  function installationTypeForComponent(component) {
    const text = ((component && component.handle) || "") + " " + ((component && component.title) || "");
    if (/concealed|ducted/i.test(text)) return "ducted";
    if (/floor[\s-]*(?:ceiling|mounted)|ceiling[\s-]*floor/i.test(text)) return "floor_ceiling";
    if (/cassette|ceiling/i.test(text)) return "ceiling";
    if (/mini[\s-]*split|air\s*handler|wall/i.test(text)) return "wall";
    return null;
  }

  function selectedOptionValue(variant, optionName) {
    const option = (variant.selectedOptions || []).find(function (item) {
      return String(item.name || "").toLowerCase() === String(optionName || "").toLowerCase();
    });
    return option ? option.value : null;
  }

  function unique(values) {
    return Array.from(new Set(values.filter(function (value) {
      return value != null && value !== "";
    })));
  }

  function normalizeConfiguration(raw) {
    const configuration = raw && typeof raw === "object" ? raw : {};
    return {
      productId: configuration.productId || null,
      handle: configuration.handle || null,
      featuredImageUrl: configuration.featuredImageUrl || null,
      mainOptions: Array.isArray(configuration.mainOptions) ? configuration.mainOptions : [],
      mainVariants: (Array.isArray(configuration.mainVariants) ? configuration.mainVariants : []).map(function (variant) {
        return Object.assign({}, variant, {
          id: numericVariantId(variant.id),
          priceCents: Number(variant.priceCents || 0),
          available: variant.available !== false,
          selectedOptions: Array.isArray(variant.selectedOptions) ? variant.selectedOptions : [],
        });
      }).filter(function (variant) {
        return variant.id;
      }),
      zones: (Array.isArray(configuration.zones) ? configuration.zones : []).map(function (zone, index) {
        const component = {
          handle: zone.componentHandle || "",
          title: zone.componentTitle || "",
        };
        return Object.assign({}, zone, {
          index: index,
          indoorBtu: positiveNumber(zone.indoorBtu) || parseIndoorBtu(zone.label) || parseIndoorBtu(zone.componentTitle),
          installLocation: zone.installLocation || installationTypeForComponent(component),
          variants: (Array.isArray(zone.variants) ? zone.variants : []).map(function (variant) {
            return Object.assign({}, variant, {
              id: numericVariantId(variant.id),
              priceCents: Number(variant.priceCents || 0),
              available: variant.available !== false,
              lineSetLengthFt: variant.lineSetLengthFt == null
                ? parseLineSetLength(variant.title)
                : normalizeLineSetLength(variant.lineSetLengthFt),
            });
          }).filter(function (variant) {
            return variant.id;
          }),
        });
      }),
    };
  }

  function configurationFromHtml(html) {
    let payload = "";
    if (typeof DOMParser !== "undefined") {
      const documentNode = new DOMParser().parseFromString(String(html || ""), "text/html");
      payload = documentNode.querySelector("[data-hvac-bundle-config]")?.textContent || "";
    } else {
      const match = String(html || "").match(/<script[^>]*data-hvac-bundle-config[^>]*>([\s\S]*?)<\/script>/i);
      payload = match ? match[1] : "";
    }
    if (!payload.trim()) throw new Error("This product configuration is not available in the current theme.");
    return normalizeConfiguration(JSON.parse(payload));
  }

  function localeRoot(value) {
    const root = String(value || "/");
    return root.endsWith("/") ? root : root + "/";
  }

  async function loadProductConfiguration(options) {
    const handle = String(options && options.handle || "").trim();
    if (!handle) throw new Error("The recommended product handle is missing.");
    const rootUrl = localeRoot(options && options.rootUrl);
    const sectionId = String(options && options.sectionId || "hvac-product-bundle-config");
    const cacheKey = rootUrl + handle + ":" + sectionId;
    if (configurationCache.has(cacheKey)) return configurationCache.get(cacheKey);

    const fetchImpl = options && options.fetchImpl || global.fetch;
    if (typeof fetchImpl !== "function") throw new Error("Product configuration requests are unavailable.");
    const url = rootUrl + "products/" + encodeURIComponent(handle) + "?section_id=" + encodeURIComponent(sectionId);
    const promise = fetchImpl(url, {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    }).then(async function (response) {
      if (!response.ok) throw new Error("Unable to load the live product configuration (" + response.status + ").");
      const configuration = configurationFromHtml(await response.text());
      if (configuration.handle && configuration.handle !== handle) {
        throw new Error("Shopify returned a different product configuration.");
      }
      return configuration;
    }).catch(function (error) {
      configurationCache.delete(cacheKey);
      throw error;
    });
    configurationCache.set(cacheKey, promise);
    return promise;
  }

  function roomInstallMatches(zone, room) {
    const expected = String(room.installLocation || "");
    if (!expected || expected === "not_sure" || expected === "ANY") return true;
    return !zone.installLocation || zone.installLocation === expected;
  }

  function roomBtuDistance(zone, room) {
    const expected = positiveNumber(room.indoorBtu);
    if (!expected || !zone.indoorBtu) return 0;
    return Math.abs(zone.indoorBtu - expected);
  }

  function assignRoomsToZones(zones, rooms) {
    if (!zones.length) return [];
    if (zones.length !== rooms.length) {
      throw new Error("This product has " + zones.length + " configurable indoor units, but the quiz has " + rooms.length + " rooms.");
    }
    const available = zones.slice();
    return rooms.map(function (room) {
      const ranked = available.map(function (zone, index) {
        return { zone: zone, index: index, distance: roomBtuDistance(zone, room) };
      }).filter(function (candidate) {
        return candidate.distance <= 600 && roomInstallMatches(candidate.zone, room);
      }).sort(function (left, right) {
        return left.distance - right.distance || left.zone.index - right.zone.index;
      });
      if (!ranked.length) {
        const expectedBtu = positiveNumber(room.indoorBtu);
        throw new Error("Room " + room.roomIndex + " cannot be mapped to a compatible " + (expectedBtu ? Math.round(expectedBtu / 1000) + "K" : "") + " indoor unit on this product.");
      }
      const selected = available.splice(ranked[0].index, 1)[0];
      return { room: room, zone: selected };
    });
  }

  function chooseZoneVariant(assignment) {
    const requestedLength = normalizeLineSetLength(assignment.room.lineSetLengthFt);
    if (!hasLineSetSelection(assignment.room.lineSetLengthFt)) {
      throw new Error("Choose a line set length for Room " + assignment.room.roomIndex + ".");
    }
    if (requestedLength === 0) {
      const withoutLineSet = (assignment.zone.variants || []).find(function (variant) {
        return variant.available !== false && normalizeLineSetLength(variant.lineSetLengthFt) === 0;
      });
      if (!withoutLineSet) {
        throw new Error("The without-line-set option for Room " + assignment.room.roomIndex + " is not available on this product.");
      }
      return Object.assign({}, assignment, {
        variant: withoutLineSet,
        lineSetSubstitution: null,
      });
    }
    const selection = closestAvailableLineSet(
      assignment.zone.variants,
      requestedLength,
      function (candidate) {
        return candidate.lineSetLengthFt;
      },
    );
    if (!selection) {
      throw new Error("The " + requestedLength + " ft line set for Room " + assignment.room.roomIndex + " is not available on this product.");
    }
    return Object.assign({}, assignment, {
      variant: selection.item,
      lineSetSubstitution: selection.substituted
        ? lineSetSubstitution(
            assignment.room.roomIndex,
            requestedLength,
            selection.length,
          )
        : null,
    });
  }

  function mainVariantAnalysis(configuration, rooms, selections) {
    let candidates = configuration.mainVariants.filter(function (variant) {
      return variant.available;
    });
    if (!candidates.length) throw new Error("This product currently has no available main variant.");

    const resolvedSelections = Object.assign({}, selections || {});
    const selectionOptions = [];
    const lineSetSubstitutions = [];
    const lineSetOptions = configuration.mainOptions.filter(function (option) {
      return lineSetOptionPattern.test(String(option.name || ""));
    });
    if (lineSetOptions.length) {
      const requestedLength = normalizeLineSetLength(rooms[0] && rooms[0].lineSetLengthFt);
      if (!hasLineSetSelection(rooms[0] && rooms[0].lineSetLengthFt)) {
        throw new Error("Choose a line set length before adding this product.");
      }
      if (configuration.zones.length) {
        const candidateWithLineSet = function (variant) {
          const lineSetLengths = lineSetOptions.map(function (option) {
            return parseLineSetLength(selectedOptionValue(variant, option.name));
          }).filter(function (length) {
            return length !== null;
          }).map(function (length) {
            return normalizeLineSetLength(length);
          });
          return {
            variant: variant,
            lineSetLength: lineSetLengths.length ? Math.min.apply(Math, lineSetLengths) : null,
          };
        };
        const exact = candidates.filter(function (variant) {
          return candidateWithLineSet(variant).lineSetLength === requestedLength;
        });
        if (exact.length) {
          candidates = exact;
        } else {
          const longer = candidates.map(candidateWithLineSet).filter(function (candidate) {
            return candidate.lineSetLength > requestedLength;
          }).sort(function (left, right) {
            return left.lineSetLength - right.lineSetLength || Number(left.variant.priceCents || 0) - Number(right.variant.priceCents || 0);
          });
          if (!longer.length) {
            throw new Error("The selected " + requestedLength + " ft line set is unavailable for this product.");
          }
          const replacementLength = longer[0].lineSetLength;
          candidates = longer.filter(function (candidate) {
            return candidate.lineSetLength === replacementLength;
          }).map(function (candidate) {
            return candidate.variant;
          });
          lineSetSubstitutions.push(
            lineSetSubstitution(rooms[0] && rooms[0].roomIndex, requestedLength, replacementLength),
          );
        }
      } else {
        lineSetOptions.forEach(function (option) {
          const values = unique(candidates.map(function (variant) {
            return selectedOptionValue(variant, option.name);
          })).filter(function (value) {
            return parseLineSetLength(value) !== null;
          });
          let selected = resolvedSelections[option.name];
          if (selected && !values.includes(selected)) {
            delete resolvedSelections[option.name];
            selected = null;
          }
          if (!selected) {
            selected = values.find(function (value) {
              return normalizeLineSetLength(parseLineSetLength(value)) === requestedLength;
            }) || null;
            if (!selected) {
              const replacement = closestAvailableLineSet(
                values.map(function (value) {
                  return { value: value, available: true };
                }),
                requestedLength,
                function (candidate) {
                  return parseLineSetLength(candidate.value);
                },
              );
              if (replacement) {
                selected = replacement.item.value;
                lineSetSubstitutions.push(
                  lineSetSubstitution(
                    rooms[0] && rooms[0].roomIndex,
                    requestedLength,
                    replacement.length,
                  ),
                );
              }
            }
            if (selected != null) resolvedSelections[option.name] = selected;
          }
          if (selected == null) {
            throw new Error(
              requestedLength === 0
                ? "The without-line-set option is unavailable for this product."
                : "The selected " + requestedLength + " ft line set is unavailable for this product.",
            );
          }
          if (values.length > 1) selectionOptions.push({ name: option.name, values: values });
          candidates = candidates.filter(function (variant) {
            return selectedOptionValue(variant, option.name) === selected;
          });
        });
      }
    }

    configuration.mainOptions.forEach(function (option) {
      if (lineSetOptionPattern.test(String(option.name || ""))) return;
      const values = unique(candidates.map(function (variant) {
        return selectedOptionValue(variant, option.name);
      }));
      if (values.length > 1) selectionOptions.push({ name: option.name, values: values });
      else if (values.length === 1) resolvedSelections[option.name] = values[0];
    });
    const missingSelections = selectionOptions.filter(function (option) {
      return !resolvedSelections[option.name] || !option.values.includes(resolvedSelections[option.name]);
    });
    const filtered = candidates.filter(function (variant) {
      return selectionOptions.every(function (option) {
        return !resolvedSelections[option.name]
          || selectedOptionValue(variant, option.name) === resolvedSelections[option.name];
      });
    });
    return {
      selectionOptions: selectionOptions,
      missingSelections: missingSelections,
      resolvedSelections: resolvedSelections,
      variant: missingSelections.length ? null : filtered[0] || null,
      lineSetSubstitutions: lineSetSubstitutions,
    };
  }

  function resolveProductConfiguration(options) {
    const configuration = normalizeConfiguration(options && options.configuration);
    const rooms = (Array.isArray(options && options.rooms) ? options.rooms : []).map(function (room, index) {
      return Object.assign({ roomIndex: index + 1 }, room);
    });
    const selections = options && options.selections || {};
    const main = mainVariantAnalysis(configuration, rooms, selections);
    const childAssignments = assignRoomsToZones(configuration.zones, rooms).map(chooseZoneVariant);
    const lineSetSubstitutions = (main.lineSetSubstitutions || []).concat(
      childAssignments.map(function (assignment) {
        return assignment.lineSetSubstitution;
      }).filter(Boolean),
    ).filter(function (substitution, index, all) {
      return all.findIndex(function (candidate) {
        return (
          candidate.roomIndex === substitution.roomIndex &&
          candidate.requestedLengthFt === substitution.requestedLengthFt &&
          candidate.replacementLengthFt === substitution.replacementLengthFt
        );
      }) === index;
    });
    const totalPriceCents = main.variant
      ? Number(main.variant.priceCents || 0) + childAssignments.reduce(function (sum, assignment) {
          return sum + Number(assignment.variant.priceCents || 0);
        }, 0)
      : null;
    return {
      configuration: configuration,
      mainVariant: main.variant,
      childAssignments: childAssignments,
      selectionOptions: main.selectionOptions,
      missingSelections: main.missingSelections,
      resolvedSelections: main.resolvedSelections,
      lineSetSubstitutions: lineSetSubstitutions,
      hasConfiguration: Boolean(childAssignments.length || main.selectionOptions.length),
      ready: Boolean(main.variant && !main.missingSelections.length),
      totalPriceCents: totalPriceCents,
    };
  }

  function createBundleId() {
    return "bundle_" + Date.now() + "_" + Math.floor(Math.random() * 10000);
  }

  function traceProperties(options) {
    const safeValue = function (value) {
      return String(value || "").trim().slice(0, 128);
    };
    const properties = {};
    const source = safeValue(options && options.source);
    const quizRunId = safeValue(options && options.quizRunId);
    const quizHandle = safeValue(options && options.quizHandle);
    if (source) properties._della_source = source;
    if (quizRunId) properties._della_quiz_run_id = quizRunId;
    if (quizHandle) properties._della_quiz_handle = quizHandle;
    return properties;
  }

  function buildCartItems(resolution, options) {
    if (!resolution || !resolution.ready || !resolution.mainVariant) {
      throw new Error("Complete the product options before adding this system to cart.");
    }
    const quantity = Math.max(1, Number(options && options.quantity) || 1);
    if (!resolution.childAssignments.length) {
      const item = { id: numericVariantId(resolution.mainVariant.id), quantity: quantity };
      const properties = traceProperties(options);
      if (Object.keys(properties).length) item.properties = properties;
      return [item];
    }
    const bundleId = options && options.bundleId || createBundleId();
    const children = resolution.childAssignments.map(function (assignment) {
      return {
        id: numericVariantId(assignment.variant.id),
        quantity: quantity,
        properties: Object.assign(traceProperties(options), {
          bundle_id: bundleId,
          "Option Label": assignment.zone.label || "Air Handler",
        }),
      };
    });
    children.push({
      id: numericVariantId(resolution.mainVariant.id),
      quantity: quantity,
      properties: Object.assign(traceProperties(options), {
        bundle_id: bundleId,
        "Product Type": "Bundle",
        main_bundle: "true",
      }),
    });
    return children;
  }

  function buildFreeMatchCartItems(system, options) {
    if (!system || !system.outdoor || !Array.isArray(system.rooms)) {
      throw new Error("This custom system configuration is incomplete.");
    }
    const integrity = system.integrity || {};
    if (
      integrity.schema !== "free-match-v2"
      || !String(integrity.manifest || "").trim()
      || !/^[a-f0-9]{64}$/.test(String(integrity.signature || ""))
    ) {
      throw new Error("This custom system is missing its secure checkout authorization.");
    }
    const bundleId = options && options.bundleId || createBundleId();
    const signature = String(system.signature || "custom-system");
    const formatCapacity = function (value) {
      const capacity = Math.round(Number(value || 0) / 1000);
      return capacity > 0 ? capacity + "K BTU" : "Matched capacity";
    };
    const formatInstallStyle = function (value) {
      const styles = {
        wall: "Wall-mounted",
        wall_mount: "Wall-mounted",
        ceiling: "Ceiling cassette",
        ceiling_cassette: "Ceiling cassette",
        ducted: "Concealed ducted",
        concealed_ducted: "Concealed ducted",
        floor_ceiling: "Floor / ceiling",
        floor: "Floor / ceiling",
      };
      const normalized = String(value || "").trim().toLowerCase();
      return styles[normalized] || "Indoor unit";
    };
    const item = function (component, properties, quantity) {
      const id = numericVariantId(component && component.variantGid);
      if (!id) {
        throw new Error("Shopify variant is missing for " + String(component && component.sku || "a component") + ".");
      }
      return {
        id: id,
        quantity: Math.max(1, Number(quantity || component.quantity) || 1),
        properties: Object.assign({
          bundle_id: bundleId,
          "Custom system": signature,
        }, properties || {}),
      };
    };
    const items = [item(system.outdoor, {
      "Component": "Outdoor unit",
      "Product Type": "Free Match Bundle",
      "Configured for": "Outdoor unit · " + formatCapacity(system.outdoorCapacityBtu || system.outdoor.capacityBtu),
      "_della_bundle_role": "outdoor",
      main_bundle: "true",
    }, 1)];
    system.rooms.forEach(function (room) {
      const capacityLabel = formatCapacity(room.requestedCapacityBtu || room.indoor.capacityBtu);
      const styleLabel = formatInstallStyle(room.requestedInstallType);
      items.push(item(room.indoor, {
        "Component": "Room " + room.roomIndex + " indoor unit",
        "Indoor style": room.requestedInstallType,
        "Indoor capacity": capacityLabel,
        "Configured for": "Room " + room.roomIndex + " · " + capacityLabel + " · " + styleLabel,
        "_della_bundle_role": "indoor",
      }, 1));
      if (room.lineSet) {
        const lineSetLength = String(room.lineSet.lineSetLengthFt || "").trim();
        items.push(item(room.lineSet, {
          "Component": "Room " + room.roomIndex + " line set",
          "Line set length": lineSetLength + " ft",
          "Configured for": "Room " + room.roomIndex + " · " + (lineSetLength ? lineSetLength + " ft line set" : "Line set"),
          "_della_bundle_role": "line_set",
        }, 1));
      }
    });
    (system.systemAccessories || []).forEach(function (component) {
      items.push(item(component, {
        "Component": "Custom system base component",
        "Line set": "Not included",
        "Configured for": "System base component · Without line set",
        "_della_bundle_role": "required_cost",
        "_della_required_component": "true",
      }, component.quantity));
    });

    const manifestCounts = {};
    items.forEach(function (cartItem) {
      const variantId = String(cartItem.id || "").trim();
      if (!variantId) return;
      manifestCounts[variantId] = (manifestCounts[variantId] || 0) + Math.max(1, Number(cartItem.quantity) || 1);
    });
    const manifest = "v1;" + Object.keys(manifestCounts).sort().map(function (variantId) {
      return variantId + "=" + manifestCounts[variantId];
    }).join(";");
    if (manifest !== integrity.manifest) {
      throw new Error("This custom system changed after it was authorized. Refresh the recommendations and try again.");
    }
    items.forEach(function (cartItem) {
      Object.assign(cartItem.properties, traceProperties(options));
      cartItem.properties._della_bundle_id = bundleId;
      cartItem.properties._della_bundle_schema = integrity.schema;
      cartItem.properties._della_bundle_manifest = integrity.manifest;
      cartItem.properties._della_bundle_signature = integrity.signature;
    });
    return items;
  }

  global.DELLAHVACCart = {
    buildCartItems: buildCartItems,
    buildFreeMatchCartItems: buildFreeMatchCartItems,
    configurationFromHtml: configurationFromHtml,
    createBundleId: createBundleId,
    loadProductConfiguration: loadProductConfiguration,
    normalizeLineSetLength: normalizeLineSetLength,
    parseIndoorBtu: parseIndoorBtu,
    parseLineSetLength: parseLineSetLength,
    resolveProductConfiguration: resolveProductConfiguration,
  };
})(typeof window !== "undefined" ? window : globalThis);
